import { NgClass } from '@angular/common';
import { Component, computed, inject, input, signal } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import {
    MatDialogActions,
    MatDialogContent,
    MatDialogRef,
    MatDialogTitle,
} from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { TranslateService } from '@ngx-translate/core';
import {
    firstValueFrom,
    isObservable,
    Observable,
    throwError,
    timeout,
} from 'rxjs';
import { SharedDialogShellActionMode } from '../../enums/shared-dialog-shell-action-mode';
import { SharedDialogAwaitTimeoutError } from '../../models/shared-dialog-await-timeout-error';
import {
    SharedDialogConfirmResult,
    SharedDialogShellConfig,
} from '../../models/shared-dialog-shell-config';
import { LoadingSpinnerComponent } from '../loading-spinner/loading-spinner.component';
import { TooltipCustomComponent } from '../tooltip-custom/tooltip-custom.component';

/** Default cap for AWAIT confirm when `confirmAwaitTimeoutMs` is omitted. */
const SHARED_DIALOG_CONFIRM_AWAIT_DEFAULT_TIMEOUT_MS = 60_000;

@Component({
    selector: 'apcam-shared-dialog-shell',
    templateUrl: './shared-dialog-shell.component.html',
    styleUrl: './shared-dialog-shell.component.scss',
    imports: [
        LoadingSpinnerComponent,
        MatButtonModule,
        MatDialogActions,
        MatDialogContent,
        MatDialogTitle,
        MatDividerModule,
        MatIconModule,
        NgClass,
        TooltipCustomComponent,
    ],
})
export class SharedDialogShellComponent {
    readonly config = input.required<SharedDialogShellConfig>();

    protected readonly dialogRef = inject(MatDialogRef, { optional: true });
    private readonly translateService = inject(TranslateService);

    protected readonly isSubmitting = signal(false);
    protected readonly showConfirmButton = computed(
        () => this.config().showConfirmButton ?? !!this.config().onConfirm
    );

    protected readonly isCancelDisabled = computed(() => {
        return this.isSubmitting() || (this.config().cancelDisabled ?? false);
    });

    protected readonly isConfirmDisabled = computed(() => {
        return this.isSubmitting() || (this.config().confirmDisabled ?? false);
    });

    protected readonly confirmMode = computed(
        () => this.config().confirmMode ?? SharedDialogShellActionMode.IMMEDIATE
    );

    protected readonly confirmButtonLabel = computed(() => {
        const cfg = this.config();
        if (this.isSubmitting() && cfg.confirmLabelWhileSubmitting) {
            return cfg.confirmLabelWhileSubmitting;
        }

        return (
            cfg.confirmLabel ??
            this.translateService.instant('shared.buttons.confirm')
        );
    });

    protected readonly cancelButtonLabel = computed(() => {
        return (
            this.config().cancelLabel ??
            this.translateService.instant('shared.buttons.cancel')
        );
    });

    protected readonly confirmButtonAriaLabel = computed((): string | null => {
        const cfg = this.config();
        if (this.isSubmitting() && cfg.confirmBusyAriaLabel) {
            return cfg.confirmBusyAriaLabel;
        }
        return null;
    });

    protected readonly confirmButtonAriaBusy = computed((): boolean | null =>
        this.isSubmitting() ? true : null
    );

    /** Trimmed confirm tooltip text; empty means no tooltip. */
    protected readonly confirmTooltipText = computed(() =>
        (this.config().confirmTooltip ?? '').trim()
    );

    protected readonly showConfirmTooltip = computed(
        () => this.confirmTooltipText().length > 0
    );

    protected readonly onConfirmClose = computed(
        () => this.config().onConfirmClose ?? true
    );

    protected onCancelClicked(): void {
        if (this.isSubmitting()) {
            return;
        }

        if (this.config().onCancel) {
            this.config().onCancel!();
            return;
        }

        this.dialogRef?.close();
    }

    /**
     * Handles Confirm click and closes dialog according to callback return type and mode.
     * Sync results always close immediately; async results follow `confirmMode`.
     * `onConfirmClose` gates whether the dialog closes after a successful confirm (sync or awaited async).
     */
    protected async onConfirmClicked(): Promise<void> {
        const confirmAction = this.config().onConfirm;

        if (!confirmAction || this.isConfirmDisabled()) {
            return;
        }

        const result = confirmAction();

        if (!this.isAsyncResult(result)) {
            if (this.onConfirmClose()) {
                this.dialogRef?.close(result);
            }
            return;
        }

        if (this.confirmMode() === SharedDialogShellActionMode.IMMEDIATE) {
            if (this.onConfirmClose()) {
                this.dialogRef?.close(result);
            }
            return;
        }

        const previousDisableClose = this.dialogRef?.disableClose;
        if (this.dialogRef) {
            this.dialogRef.disableClose = true;
        }
        this.isSubmitting.set(true);
        try {
            const resolvedResult = await this.resolveAsyncConfirmResult(result);
            if (this.onConfirmClose()) {
                this.dialogRef?.close(resolvedResult);
            }
        } catch (error) {
            this.config().onAwaitError?.(error);
        } finally {
            this.isSubmitting.set(false);
            if (this.dialogRef) {
                this.dialogRef.disableClose = previousDisableClose;
            }
        }
    }

    /**
     * Resolves Promise/Observable results for await mode, optionally capped by
     * {@link SharedDialogShellConfig.confirmAwaitTimeoutMs}.
     */
    private async resolveAsyncConfirmResult<TResult>(
        result: Promise<TResult> | Observable<TResult>
    ): Promise<TResult> {
        const timeoutMs = this.getEffectiveConfirmAwaitTimeoutMs();

        if (isObservable(result)) {
            return await firstValueFrom(
                this.observableWithAwaitTimeout(result, timeoutMs)
            );
        }

        return await this.promiseWithAwaitTimeout(result, timeoutMs);
    }

    /**
     * When `timeoutMs` is set, fails the stream if the first value does not arrive in time.
     * When `timeoutMs` is null, the source is unchanged (no timeout).
     */
    private observableWithAwaitTimeout<T>(
        source: Observable<T>,
        timeoutMs: number | null
    ): Observable<T> {
        if (timeoutMs === null) {
            return source;
        }

        return source.pipe(
            timeout({
                first: timeoutMs,
                with: () =>
                    throwError(() => this.createAwaitTimeoutError(timeoutMs)),
            })
        );
    }

    private async promiseWithAwaitTimeout<T>(
        promise: Promise<T>,
        timeoutMs: number | null
    ): Promise<T> {
        if (timeoutMs === null) {
            return await promise;
        }

        return await this.racePromiseWithTimeout(promise, timeoutMs);
    }

    private getEffectiveConfirmAwaitTimeoutMs(): number | null {
        const v = this.config().confirmAwaitTimeoutMs;
        if (v === null || v === 0) {
            return null;
        }
        if (v === undefined) {
            return SHARED_DIALOG_CONFIRM_AWAIT_DEFAULT_TIMEOUT_MS;
        }
        return v;
    }

    private createAwaitTimeoutError(
        timeoutMs: number
    ): SharedDialogAwaitTimeoutError {
        return new SharedDialogAwaitTimeoutError(
            timeoutMs,
            this.translateService.instant('shared.dialog.confirmAwaitTimeout')
        );
    }

    private racePromiseWithTimeout<T>(
        promise: Promise<T>,
        timeoutMs: number
    ): Promise<T> {
        let timeoutId: ReturnType<typeof setTimeout> | undefined;
        const timeoutPromise = new Promise<never>((_, reject) => {
            timeoutId = setTimeout(
                () => reject(this.createAwaitTimeoutError(timeoutMs)),
                timeoutMs
            );
        });
        return Promise.race([promise, timeoutPromise]).finally(() => {
            if (timeoutId !== undefined) {
                clearTimeout(timeoutId);
            }
        });
    }

    /**
     * Returns true when callback result is Promise-like or Observable.
     */
    private isAsyncResult<TResult>(
        result: SharedDialogConfirmResult<TResult> | void
    ): result is Promise<TResult> | Observable<TResult> {
        return this.isPromiseLike(result) || isObservable(result);
    }

    /**
     * True for native {@link Promise} instances first, then minimal thenables (A+ style).
     * `instanceof Promise` rejects most accidental `{ then: fn }` objects; the thenable
     * branch keeps cross-realm / subclass interop that still needs `await`.
     */
    private isPromiseLike<TResult>(
        value: SharedDialogConfirmResult<TResult> | void
    ): value is Promise<TResult> {
        if (value === null || value === undefined) {
            return false;
        }
        if (value instanceof Promise) {
            return true;
        }
        if (typeof value !== 'object') {
            return false;
        }
        const then = (value as { then?: unknown }).then;
        return typeof then === 'function';
    }
}
